import {
    ColumnDefinition,
    ColumnDefinitionsDictionary,
    getColDefsForColumnDefinitionsDictionary,
    ValueType,
} from '@instinet-analytics/semantic-ui-components';
import { IndigoAppContextType } from 'app/AppContext.tsx';
import { ColDef } from 'ag-grid-community';
import React from 'react';
import { BucketedDataItem } from 'utils/timeseries/bucket.ts';

export type OrderVolumeProfileGridColumn =
    | 'startTime'
    | 'endTime'
    | 'vol'
    | 'auctVol'
    | 'contVol'
    | 'cumVol'
    | 'wgt'
    | 'auctWgt'
    | 'contWgt'
    | 'cumWgt';

export const orderVolumeProfileGridColumnsDefinitionDictionary: ColumnDefinitionsDictionary<OrderVolumeProfileGridColumn> =
    {
        startTime: {
            field: 'startTime',
            name: 'Start Time',
            valueType: ValueType.TIME,
            headerTooltip: 'Bucket Start Time',
            width: 70,
        },
        endTime: {
            field: 'endTime',
            name: 'End Time',
            valueType: ValueType.TIME,
            headerTooltip: 'Bucket End Time',
            width: 70,
        },
        vol: {
            field: 'vol',
            name: 'Volume',
            valueType: ValueType.NUMBER,
            precision: 0,
            headerTooltip: 'Volume',
            width: 110,
        },
        auctVol: {
            field: 'auctVol',
            name: 'Auction Volume',
            valueType: ValueType.NUMBER,
            precision: 0,
            headerTooltip: 'Volume from an Auction',
            width: 110,
        },
        contVol: {
            field: 'contVol',
            name: 'Continuous Volume',
            valueType: ValueType.NUMBER,
            precision: 0,
            headerTooltip: 'Volume from Continuous Trading',
            width: 110,
        },
        cumVol: {
            field: 'cumVol',
            name: 'Cumulative Volume',
            valueType: ValueType.NUMBER,
            precision: 0,
            headerTooltip:
                'Cumulative order volume up to the end of this time bucket',
            width: 110,
        },
        wgt: {
            field: 'wgt',
            name: 'Weight',
            valueType: ValueType.NUMBER,
            precision: 2,
            isPercentage: true,
            headerTooltip:
                'Normalised volume profile weight in this time bucket',
            width: 110,
        },
        auctWgt: {
            field: 'auctWgt',
            name: 'Auction Weight',
            valueType: ValueType.NUMBER,
            isPercentage: true,
            precision: 2,
            headerTooltip:
                'Normalised auction volume weight in this time bucket',
            width: 110,
        },
        contWgt: {
            field: 'contWgt',
            name: 'Continuous Trading Weight',
            valueType: ValueType.NUMBER,
            precision: 2,
            isPercentage: true,
            headerTooltip:
                'Normalised continuous trading weight in this time bucket',
            width: 110,
        },
        cumWgt: {
            field: 'cumWgt',
            name: 'Cumulative Weight',
            valueType: ValueType.NUMBER,
            isPercentage: true,
            precision: 2,
            headerTooltip:
                'Cumulative normalised volume profile weight up to the end of this time bucket',
            width: 110,
        },
    };

export const orderVolumeProfileColumnsDefinitions: ColumnDefinition[] =
    Object.values(orderVolumeProfileGridColumnsDefinitionDictionary);

export function getOrderVolumeProfileColDefs(context: IndigoAppContextType) {
    const colDefs = getColDefsForColumnDefinitionsDictionary(
        context,
        orderVolumeProfileGridColumnsDefinitionDictionary
    );

    enrichTimeColumn(context, colDefs, 'startTime');
    enrichTimeColumn(context, colDefs, 'endTime');

    return colDefs;
}

const getTimeDisplayValue = (
    context: IndigoAppContextType,
    field: keyof BucketedDataItem,
    data?: BucketedDataItem
) => {
    if (!data) {
        return '';
    }
    const value = data[field];

    if (value) {
        return context.timeFormat.toTimeMinutes(value);
    }

    return value ?? '';
};

const timeCellRenderer =
    (context: IndigoAppContextType, field: keyof BucketedDataItem) =>
    // eslint-disable-next-line react/display-name
    (props: { data: BucketedDataItem }) => {
        return <span>{getTimeDisplayValue(context, field, props.data)}</span>;
    };

function enrichTimeColumn(
    context: IndigoAppContextType,
    cols: ColDef[],
    field: keyof BucketedDataItem
) {
    const colDef = cols.find((f) => f.field === field);
    if (!colDef) {
        return;
    }

    colDef.cellRenderer = timeCellRenderer(context, field);
}
